import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { SUPPORTED_LANGUAGES, SupportedLanguage, changeLanguage } from '../../i18n';
import { closeApp } from '../../utils/closeApp';
import { acceptPrivacyConsent } from '../../utils/privacyConsent';
import './PrivacyConsent.css';

const TERMS_URL = 'https://ativacao.auroraflux.net/termos-de-uso';

// Focus order: 8 lang buttons + accept + decline
type FocusTarget =
  | 'lang:pt-BR' | 'lang:en' | 'lang:es' | 'lang:fr'
  | 'lang:de' | 'lang:ar' | 'lang:it' | 'lang:tr'
  | 'accept' | 'decline';

const FOCUS_ORDER: FocusTarget[] = [
  'lang:pt-BR', 'lang:en', 'lang:es', 'lang:fr',
  'lang:de', 'lang:ar', 'lang:it', 'lang:tr',
  'accept', 'decline',
];

const LANG_LABELS: Record<SupportedLanguage, string> = {
  'pt-BR': 'PT-BR',
  en: 'EN',
  es: 'ES',
  fr: 'FR',
  de: 'DE',
  ar: 'AR',
  it: 'IT',
  tr: 'TR',
};

interface Props {
  onAccept: () => void;
}

export function PrivacyConsent({ onAccept }: Props) {
  const { t, i18n } = useTranslation();
  const activeLang = i18n.language as SupportedLanguage;

  const [focused, setFocused] = useState<FocusTarget>('accept');

  const btnRefs = useRef<Partial<Record<FocusTarget, HTMLButtonElement | null>>>({});
  const stateRef = useRef({ focused, onAccept, activeLang });

  useEffect(() => {
    stateRef.current = { focused, onAccept, activeLang };
  });

  // Initial focus — accept button, retry for Tizen
  useEffect(() => {
    function tryFocus() {
      const el = btnRefs.current['accept'];
      if (el) el.focus();
    }
    tryFocus();
    const id = setTimeout(tryFocus, 120);
    return () => clearTimeout(id);
  }, []);

  // Sync DOM focus to logical state
  useEffect(() => {
    const el = btnRefs.current[focused];
    if (el) el.focus();
  }, [focused]);

  // Stable keyboard handler on window
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      var key = (e as any).key as string | undefined;
      var code = ((e as any).keyCode || (e as any).which || 0) as number;

      // Chrome 47 key name normalization
      if (!key || key === 'Unidentified') {
        if (code === 37) key = 'ArrowLeft';
        else if (code === 38) key = 'ArrowUp';
        else if (code === 39) key = 'ArrowRight';
        else if (code === 40) key = 'ArrowDown';
        else if (code === 13) key = 'Enter';
        else if (code === 32) key = 'Space';
        else if (code === 8) key = 'Backspace';
      }
      // Chrome 47 alternate arrow names
      if (key === 'Left') key = 'ArrowLeft';
      if (key === 'Right') key = 'ArrowRight';
      if (key === 'Up') key = 'ArrowUp';
      if (key === 'Down') key = 'ArrowDown';

      if (key === 'Tab') return;

      const cur = stateRef.current.focused;
      const curIdx = FOCUS_ORDER.indexOf(cur);

      if (key === 'ArrowLeft' || key === 'ArrowUp') {
        e.preventDefault();
        if (typeof (e as any).stopImmediatePropagation === 'function') {
          (e as any).stopImmediatePropagation();
        }
        const prev = curIdx > 0 ? curIdx - 1 : FOCUS_ORDER.length - 1;
        setFocused(FOCUS_ORDER[prev]);
        return;
      }

      if (key === 'ArrowRight' || key === 'ArrowDown') {
        e.preventDefault();
        if (typeof (e as any).stopImmediatePropagation === 'function') {
          (e as any).stopImmediatePropagation();
        }
        const next = curIdx < FOCUS_ORDER.length - 1 ? curIdx + 1 : 0;
        setFocused(FOCUS_ORDER[next]);
        return;
      }

      if (key === 'Enter' || key === 'Space') {
        e.preventDefault();
        if (typeof (e as any).stopImmediatePropagation === 'function') {
          (e as any).stopImmediatePropagation();
        }
        const target = stateRef.current.focused;
        if (target.startsWith('lang:')) {
          const lang = target.replace('lang:', '') as SupportedLanguage;
          changeLanguage(lang);
          setFocused('accept');
          return;
        }
        if (target === 'accept') {
          acceptPrivacyConsent();
          stateRef.current.onAccept();
          return;
        }
        if (target === 'decline') {
          closeApp();
          return;
        }
        return;
      }

      // Back key — same as decline
      const isBack =
        key === 'Escape' || key === 'GoBack' ||
        code === 27 || code === 10009 || code === 461;
      if (isBack) {
        e.preventDefault();
        if (typeof (e as any).stopImmediatePropagation === 'function') {
          (e as any).stopImmediatePropagation();
        }
        closeApp();
      }
    }

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function makeRef(target: FocusTarget) {
    return (el: HTMLButtonElement | null) => {
      btnRefs.current[target] = el;
    };
  }

  function isFocused(target: FocusTarget) {
    return focused === target;
  }

  function handleAccept() {
    acceptPrivacyConsent();
    onAccept();
  }

  return (
    <div className="privacy-screen">

      {/* Top bar */}
      <div className="privacy-screen__top-bar">
        <span className="privacy-screen__brand">AURORA FLUX</span>
        <div className="privacy-screen__lang-bar">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang}
              ref={makeRef(`lang:${lang}` as FocusTarget)}
              type="button"
              className={
                'privacy-screen__lang-btn' +
                (activeLang === lang ? ' privacy-screen__lang-btn--active' : '') +
                (isFocused(`lang:${lang}` as FocusTarget) ? ' privacy-screen__lang-btn--focused' : '')
              }
              onClick={() => { changeLanguage(lang); setFocused('accept'); }}
              onFocus={() => setFocused(`lang:${lang}` as FocusTarget)}
              tabIndex={0}
            >
              {LANG_LABELS[lang]}
            </button>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="privacy-screen__header">
        <p className="privacy-screen__eyebrow">{t('privacy.eyebrow')}</p>
        <h1 className="privacy-screen__title">{t('privacy.title')}</h1>
        <p className="privacy-screen__subtitle">{t('privacy.subtitle')}</p>
      </div>

      {/* Columns */}
      <div className="privacy-screen__content">

        {/* Info card */}
        <div className="privacy-screen__info-card">
          <div>
            <ul className="privacy-screen__info-list">
              <li>{t('privacy.item1')}</li>
              <li>{t('privacy.item2')}</li>
              <li>{t('privacy.item3')}</li>
            </ul>
            <p className="privacy-screen__legal">{t('privacy.legal')}</p>
          </div>

          {/* Terms + QR */}
          <div className="privacy-screen__terms-block">
            <div className="privacy-screen__qr-wrapper">
              <QRCode value={TERMS_URL} size={58} level="M" bgColor="#ffffff" fgColor="#000000" />
            </div>
            <div className="privacy-screen__terms-text">
              <p className="privacy-screen__terms-label">{t('privacy.termsLabel')}</p>
              <p className="privacy-screen__terms-url">ativacao.auroraflux.net/termos-de-uso</p>
              <p className="privacy-screen__terms-scan-hint">{t('privacy.termsScanHint')}</p>
            </div>
          </div>
        </div>

        {/* Action card */}
        <div className="privacy-screen__action-card">
          <p className="privacy-screen__nav-hint">{t('privacy.navHint')}</p>

          <button
            ref={makeRef('accept')}
            type="button"
            className={
              'privacy-screen__btn privacy-screen__btn--accept' +
              (isFocused('accept') ? ' privacy-screen__btn--focused' : '')
            }
            onClick={handleAccept}
            onFocus={() => setFocused('accept')}
            tabIndex={0}
          >
            {t('privacy.accept')}
          </button>

          <button
            ref={makeRef('decline')}
            type="button"
            className={
              'privacy-screen__btn privacy-screen__btn--decline' +
              (isFocused('decline') ? ' privacy-screen__btn--focused' : '')
            }
            onClick={closeApp}
            onFocus={() => setFocused('decline')}
            tabIndex={0}
          >
            {t('privacy.decline')}
          </button>
        </div>

      </div>

      {/* Footer */}
      <p className="privacy-screen__footer">{t('privacy.footer')}</p>

    </div>
  );
}
